/**
 * /leadership — dedicated executive Leadership page featuring the Founder profile.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowUpRight } from "lucide-react";
import { LangCtx, LanguageSwitcher } from "@/i18n/neunexus";
import FounderCard from "@/pages/bsgotech/FounderCard";

const P = {
  bg:      "#080B12",
  bg2:     "#0A0F1A",
  border:  "rgba(255,255,255,0.08)",
  ink:     "#E8ECF0",
  dim:     "rgba(232,236,240,0.68)",
  silver:  "#C0C7CE",
  green:   "#22C55E",
  blue:    "#3B82F6",
};

const COPY = {
  en: {
    docTitle: "Leadership · BSGOTECH",
    back: "BSGOTECH",
    eyebrow: "Leadership",
    title: "The vision behind the ecosystem.",
    subtitle: "BSGOTECH is a European technology company. The Founder leads the long-term strategic direction of the ecosystem — BSGOTECH, NeuNexus, RENALIX and NeuGreen Energy.",
    contextTitle: "How the leadership fits the ecosystem",
    contextLines: [
      "BSGOTECH is the parent technology company.",
      "NeuNexus is the industrial innovation platform.",
      "RENALIX and NeuGreen Energy are strategic operational platforms.",
      "The Founder leads the long-term vision and development of the ecosystem.",
    ],
    manualCta: "Read the Founder Handbook (Vol. 09)",
  },
  de: {
    docTitle: "Leadership · BSGOTECH",
    back: "BSGOTECH",
    eyebrow: "Leadership",
    title: "Die Vision hinter dem Ökosystem.",
    subtitle: "BSGOTECH ist ein europäisches Technologieunternehmen. Der Gründer verantwortet die langfristige strategische Ausrichtung des Ökosystems — BSGOTECH, NeuNexus, RENALIX und NeuGreen Energy.",
    contextTitle: "Wie die Führung ins Ökosystem passt",
    contextLines: [
      "BSGOTECH ist die Muttergesellschaft der Technologie.",
      "NeuNexus ist die industrielle Innovationsplattform.",
      "RENALIX und NeuGreen Energy sind strategische operative Plattformen.",
      "Der Gründer verantwortet die langfristige Vision und Entwicklung des Ökosystems.",
    ],
    manualCta: "Founder-Handbuch (Band 09) lesen",
  },
  pt: {
    docTitle: "Liderança · BSGOTECH",
    back: "BSGOTECH",
    eyebrow: "Liderança",
    title: "A visão por trás do ecossistema.",
    subtitle: "A BSGOTECH é uma empresa tecnológica europeia. O Fundador conduz a direcção estratégica de longo prazo do ecossistema — BSGOTECH, NeuNexus, RENALIX e NeuGreen Energy.",
    contextTitle: "Como a liderança se enquadra no ecossistema",
    contextLines: [
      "A BSGOTECH é a empresa-mãe tecnológica.",
      "A NeuNexus é a plataforma de inovação industrial.",
      "A RENALIX e a NeuGreen Energy são plataformas operacionais estratégicas.",
      "O Fundador conduz a visão e o desenvolvimento de longo prazo do ecossistema.",
    ],
    manualCta: "Ler o Manual do Fundador (Vol. 09)",
  },
};

const useL = () => {
  const { lang } = React.useContext(LangCtx);
  return { lang, t: COPY[lang] || COPY.en };
};

export default function LeadershipPage() {
  const { lang, t } = useL();
  React.useEffect(() => { document.title = t.docTitle; }, [t.docTitle]);

  return (
    <div className="relative min-h-screen" style={{ background: P.bg, color: P.ink }} data-testid="page-leadership">
      <LanguageSwitcher />

      {/* Top bar */}
      <div className="sticky top-0 z-40" style={{ background: "rgba(8,11,18,0.85)", borderBottom: `1px solid ${P.border}`, backdropFilter: "blur(12px)" }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 text-[12px]" style={{ color: P.silver }} data-testid="leadership-back-home">
            <ChevronRight size={13} className="rotate-180" />
            <span className="uppercase tracking-[0.28em]">{t.back}</span>
          </Link>
          <div className="hidden md:block text-[11px] uppercase tracking-[0.28em]" style={{ color: P.silver }}>
            {t.eyebrow}
          </div>
          <div />
        </div>
      </div>

      {/* Hero header — no photo here, corporate identity comes first */}
      <header className="border-b" style={{ borderColor: P.border }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="text-[10px] uppercase tracking-[0.42em] font-semibold" style={{ color: P.green }}>{t.eyebrow}</div>
          <h1 className="mt-5 font-semibold tracking-tight text-[32px] sm:text-[42px] lg:text-[54px] leading-[1.05] max-w-4xl"
            style={{ color: "#fff", letterSpacing: "-0.02em" }}>
            {t.title}
          </h1>
          <p className="mt-5 max-w-3xl text-[13.5px] leading-relaxed" style={{ color: P.dim }}>
            {t.subtitle}
          </p>
        </div>
      </header>

      {/* Founder card */}
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-16 space-y-8">
        <FounderCard variant="full" />

        {/* Ecosystem context block */}
        <div className="rounded-xl p-6 lg:p-8" style={{ background: `linear-gradient(135deg, ${P.green}0a 0%, ${P.bg2} 55%)`, border: `1px solid ${P.green}44` }}>
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.green }}>{t.contextTitle}</div>
          <ul className="mt-4 space-y-1.5 text-[13.5px] leading-relaxed" style={{ color: P.ink }}>
            {t.contextLines.map((line, i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-2 w-1 h-1 rounded-full shrink-0" style={{ background: P.green }} />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/manual"
            data-testid="leadership-manual-cta"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 text-[12.5px] font-semibold rounded-md transition hover:bg-white/5"
            style={{ border: `1px solid ${P.border}`, color: P.ink }}
          >
            {t.manualCta} <ArrowUpRight size={13} />
          </Link>
        </div>
      </main>
    </div>
  );
}
